var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { DeleteModelMessage, LinkToModelFacet, NetworkFacet, RemoveSubModelMessage } from "flavr-common";
import { Matrix4 } from "flavr-math";
import { FacetHandler, FacetRemoveHandler, MessageHandler, ModelEventHandler, ModelEventType, NewModule } from "@modules/module";
import { modelPool, computeLocalMatrixFromWorldMatrix } from "@modules/model";
import { NO_MESSAGING } from "@modules/model/types";
import { coreUserService } from "@modules/users";
const TMP_NEW_CHILD_WORLD_MATRIX = new Matrix4();
const TMP_NEW_CHILD_LOCAL_MATRIX = new Matrix4();
/**
 * Keeps linked child models aligned with a target model so relative attachments can be expressed as reusable facet data instead of imperative update code.
 *
 * Facets:
 * - `LinkToModelFacet` - Describes which target model another model should follow and optionally stores the relative offset; adding it immediately recomputes the linked model's transform, and removing it (once the removal isn't stale) unregisters the link.
 *
 * Messages:
 * - `RemoveSubModelMessage` and `DeleteModelMessage` - Unregister a removed model's own link, and refresh any models still linked to it as a target.
 *
 * Events:
 * - `ModelEventType.TRANSFORM_UPDATED` - Recomputes the transform of every model linked to the model whose transform just changed.
 */
export class LinkingModule extends NewModule {
    constructor() {
        super("LinkingModule");
        this.links = new Map();
        this.targetToChildren = new Map();
    }
    handleLinkToModelFacet(uid, facet, meta) {
        this.unregisterLink(uid);
        const child = modelPool.getModel(uid);
        if (child === undefined)
            return;
        if (facet.targetUID === undefined)
            return;
        if (facet.targetUID === uid) {
            console.warn(`[linking] Cannot link model ${uid} to itself`);
            return;
        }
        const info = {
            createdTime: meta.timeStamp,
            targetUID: facet.targetUID,
            offsetMatrix: facet.offsetMatrix !== undefined
                ? new Matrix4().fromArray(facet.offsetMatrix)
                : undefined
        };
        this.links.set(uid, info);
        this.registerReverseLookup(this.targetToChildren, info.targetUID, uid);
        this.updateTransformOfLinkedModels(uid, info);
    }
    handleRemoveLinkToModelFacet(uid, _facetType, meta) {
        const info = this.links.get(uid);
        if (info === undefined)
            return;
        if (info.createdTime > meta.timeStamp)
            return;
        this.unregisterLink(uid);
    }
    handleRemoveModelMessage(msg) {
        this.unregisterLink(msg.uid);
        const targetChildren = this.targetToChildren.get(msg.uid);
        if (targetChildren !== undefined) {
            for (const childUid of targetChildren) {
                const info = this.links.get(childUid);
                if (info === undefined)
                    continue;
                this.updateTransformOfLinkedModels(childUid, info);
            }
        }
    }
    handleTransformUpdated(model) {
        const children = this.targetToChildren.get(model.getUID());
        if (children === undefined)
            return;
        for (const childUid of children) {
            const info = this.links.get(childUid);
            if (info === undefined)
                continue;
            this.updateTransformOfLinkedModels(childUid, info);
        }
    }
    /// Update transform of linked models ///
    updateTransformOfLinkedModels(childUid, info) {
        var _a;
        const child = modelPool.getModel(childUid);
        if (child === undefined) {
            this.unregisterLink(childUid);
            return;
        }
        const networkOwnerId = (_a = child.getFacet(NetworkFacet)) === null || _a === void 0 ? void 0 : _a.ownerId;
        // A peer must use the owner's streamed pose instead of recomputing from its interpolated controller.
        if (networkOwnerId !== undefined && networkOwnerId !== coreUserService.getLocalUser().clientIdx)
            return;
        const target = (info.targetUID !== undefined) ? modelPool.getModel(info.targetUID) : undefined;
        if (target === undefined) {
            console.warn(`[linking] Cannot update link for model ${childUid}, because target ${info.targetUID} doesn't exist`);
            return;
        }
        TMP_NEW_CHILD_WORLD_MATRIX.copy(target.getWorldTransform());
        if (info.offsetMatrix !== undefined) {
            TMP_NEW_CHILD_WORLD_MATRIX.multiply(info.offsetMatrix);
        }
        computeLocalMatrixFromWorldMatrix(child, TMP_NEW_CHILD_WORLD_MATRIX, TMP_NEW_CHILD_LOCAL_MATRIX);
        child.setTransform(TMP_NEW_CHILD_LOCAL_MATRIX, NO_MESSAGING);
    }
    /// Link registration ///
    registerReverseLookup(map, key, childUid) {
        if (key === undefined)
            return;
        let children = map.get(key);
        if (children === undefined) {
            children = new Set();
            map.set(key, children);
        }
        children.add(childUid);
    }
    unregisterReverseLookup(map, key, childUid) {
        if (key === undefined)
            return;
        const children = map.get(key);
        if (children === undefined)
            return;
        children.delete(childUid);
        if (children.size === 0) {
            map.delete(key);
        }
    }
    unregisterLink(childUid) {
        const info = this.links.get(childUid);
        if (info === undefined)
            return;
        this.unregisterReverseLookup(this.targetToChildren, info.targetUID, childUid);
        this.links.delete(childUid);
    }
}
__decorate([
    FacetHandler(LinkToModelFacet)
], LinkingModule.prototype, "handleLinkToModelFacet", null);
__decorate([
    FacetRemoveHandler(LinkToModelFacet)
], LinkingModule.prototype, "handleRemoveLinkToModelFacet", null);
__decorate([
    MessageHandler(RemoveSubModelMessage, DeleteModelMessage)
], LinkingModule.prototype, "handleRemoveModelMessage", null);
__decorate([
    ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
], LinkingModule.prototype, "handleTransformUpdated", null);
